import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PaperPlaneRight, Sparkle, User } from "@phosphor-icons/react";
import { itinerariesApi, streamAI } from "@/lib/apiClient";
import AIMarkdown from "@/components/AIMarkdown";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function ConciergeChat({ itineraryId }) {
    const { t } = useTranslation();
    const [history, setHistory] = useState([]);
    const [text, setText] = useState("");
    const [streaming, setStreaming] = useState("");
    const [busy, setBusy] = useState(false);
    const feedRef = useRef(null);

    useEffect(() => {
        itinerariesApi.chatHistory(itineraryId).then((r) => setHistory(r.history || [])).catch(() => {});
    }, [itineraryId]);

    useEffect(() => {
        feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: "smooth" });
    }, [history, streaming]);

    const submit = async (e) => {
        e?.preventDefault?.();
        const msg = text.trim();
        if (!msg || busy) return;
        setText("");
        setBusy(true);
        setStreaming("");
        setHistory((prev) => [...prev, { role: "user", text: msg, at: new Date().toISOString() }]);
        try {
            const s = streamAI(`/itineraries/${itineraryId}/chat`, { message: msg });
            let acc = "";
            for await (const chunk of s) {
                acc += chunk;
                setStreaming(acc);
            }
            setHistory((prev) => [...prev, { role: "assistant", text: acc, at: new Date().toISOString() }]);
            setStreaming("");
        } catch {
            toast.error(t("concierge.offlineToast"));
        } finally { setBusy(false); }
    };

    const suggested = t("concierge.suggestions", { returnObjects: true }) || [];

    return (
        <div className="mt-10" data-testid="concierge-chat">
            <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-full grid place-items-center" style={{ background: "rgba(212,175,55,0.16)", border: "1px solid rgba(212,175,55,0.4)" }}>
                    <Sparkle size={14} weight="fill" color="#D4AF37" />
                </div>
                <div>
                    <div className="overline" style={{ color: "var(--gold)" }}>{t("concierge.title")}</div>
                    <div className="font-serif text-xl text-white leading-tight">{t("concierge.subtitle")}</div>
                </div>
            </div>

            <div
                ref={feedRef}
                className="rounded-3xl p-5 md:p-6 max-h-[420px] overflow-y-auto space-y-4"
                style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
                {history.length === 0 && !streaming && (
                    <div className="text-white/50 text-sm">
                        <p className="mb-4">{t("concierge.introPrompt")}</p>
                        <div className="flex flex-wrap gap-2">
                            {suggested.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setText(s)}
                                    className="text-xs px-3 py-1.5 rounded-full"
                                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(212,175,55,0.2)", color: "rgba(255,255,255,0.75)" }}
                                    data-testid="concierge-suggestion"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {history.map((h, i) => (
                    <ChatBubble key={i} role={h.role} text={h.text} />
                ))}
                {streaming && <ChatBubble role="assistant" text={streaming} streaming />}
            </div>

            <form onSubmit={submit} className="mt-3 flex gap-2">
                <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t("concierge.placeholder")}
                    className="flex-1 rounded-full px-5 py-3 text-sm text-white focus:outline-none"
                    style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)" }}
                    data-testid="concierge-input"
                />
                <button
                    type="submit"
                    disabled={busy || !text.trim()}
                    className="w-12 h-12 rounded-full grid place-items-center disabled:opacity-50"
                    style={{ background: "var(--gold)", color: "var(--navy)" }}
                    data-testid="concierge-send"
                    aria-label={t("concierge.send")}
                >
                    <PaperPlaneRight size={14} weight="fill" />
                </button>
            </form>
        </div>
    );
}

function ChatBubble({ role, text, streaming }) {
    const isUser = role === "user";
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className={`flex gap-3 items-start ${isUser ? "flex-row-reverse" : ""}`}
        >
            <div
                className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                style={{
                    background: isUser ? "rgba(255,255,255,0.08)" : "rgba(212,175,55,0.14)",
                    border: `1px solid ${isUser ? "rgba(255,255,255,0.12)" : "rgba(212,175,55,0.4)"}`,
                }}
            >
                {isUser ? <User size={12} /> : <Sparkle size={12} weight="fill" color="#D4AF37" />}
            </div>
            <div
                className={`rounded-2xl px-4 py-3 max-w-[80%] ${isUser ? "text-white" : ""}`}
                style={{
                    background: isUser ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${isUser ? "rgba(212,175,55,0.25)" : "rgba(255,255,255,0.06)"}`,
                }}
                data-testid={`concierge-bubble-${role}`}
            >
                {isUser ? (
                    <div className="text-sm text-white whitespace-pre-wrap">{text}</div>
                ) : (
                    <AIMarkdown className="text-sm">{text || (streaming ? "…" : "")}</AIMarkdown>
                )}
            </div>
        </motion.div>
    );
}

